// Deterministic randomness for IR synthesis.
//
// Every random draw the synthesizer makes (modal phases, velvet-noise impulse
// positions/signs) is seeded from the room's own acoustic data, so rebuilding
// an IR with the same inputs yields a byte-identical result. That determinism
// is what lets before/after A/B comparisons be honest: the only thing that
// changes between two IRs is the physics, never the noise realization.

import type { RoomAcoustics } from './types';

/**
 * Mulberry32 — small, fast, well-distributed seeded PRNG. Returns a function
 * that yields uniform floats in [0, 1).
 */
export function mulberry32(seed: number): () => number {
  let s = (seed >>> 0) || 0x1234abcd;
  return function (): number {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** FNV-style mix of one float into a running 32-bit hash. */
function mix(h: number, x: number): number {
  const xi = Math.round(Number(x) * 1e6) | 0;
  return Math.imul(h ^ xi, 16777619) >>> 0;
}

/**
 * Hash a room's acoustic curves into a stable integer seed. Any change to the
 * freq response or RT60 data reseeds the whole IR; identical data reproduces it.
 */
export function seedFromRoom(room: RoomAcoustics): number {
  let h = 0x811c9dc5;
  const { freqs, mag } = room.freqResponse;
  for (let i = 0; i < freqs.length; i++) {
    h = mix(h, freqs[i]);
    h = mix(h, mag[i]);
  }
  const fine = room.rt60Fine;
  for (let i = 0; i < fine.freqs.length; i++) {
    h = mix(h, fine.freqs[i]);
    h = mix(h, fine.rt60[i]);
  }
  const keys = Object.keys(room.rt60Bands).map(Number).sort((a, b) => a - b);
  for (const k of keys) {
    h = mix(h, k);
    h = mix(h, room.rt60Bands[String(k)]);
  }
  return h || 1;
}
